import type { MediaAttachment, PublicAccount } from '@postpilot/core/domain';
import { Heart, MessageCircle, Repeat2, Share } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/primitives';
import { cn } from '@/lib/utils';

/**
 * A live preview of the post as it will appear on X.
 *
 * The point is not decoration — it is catching the mistakes a plain textarea
 * hides: a line break that lands badly, a link that eats the last line, an
 * image whose crop cuts off the subject. Seeing it in the real shape is how you
 * notice.
 *
 * Renders as the first selected account so the avatar and handle are right; a
 * bulk post shows a count of the rest.
 */
export function PostPreview({
  content,
  media,
  accounts,
}: {
  content: string;
  media: MediaAttachment[];
  accounts: PublicAccount[];
}) {
  const account = accounts[0];
  const others = accounts.length - 1;

  return (
    <div className="bg-card rounded-xl border p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-muted-foreground text-xs font-medium">Preview</span>
        {others > 0 && (
          <span className="text-muted-foreground text-xs">
            and {others} other account{others === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div className="flex gap-3">
        <Avatar className="size-10 shrink-0">
          <AvatarImage src={account?.avatarUrl ?? undefined} alt="" />
          <AvatarFallback>
            {account ? account.username.slice(0, 2).toUpperCase() : '??'}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm">
            <span className="truncate font-semibold">
              {account?.displayName ?? account?.username ?? 'Your account'}
            </span>
            <span className="text-muted-foreground truncate">
              @{account?.username ?? 'username'}
            </span>
            <span className="text-muted-foreground">· now</span>
          </div>

          {/*
            `whitespace-pre-wrap` preserves the user's line breaks — without it
            the preview collapses them and lies about what will be posted.
            `break-words` stops a long unbroken URL from blowing out the layout.
          */}
          <p
            className={cn(
              'mt-0.5 whitespace-pre-wrap break-words text-sm leading-normal',
              !content && 'text-muted-foreground',
            )}
          >
            {content || 'What’s happening?'}
          </p>

          {media.length > 0 && (
            <div
              className={cn(
                'mt-3 grid gap-0.5 overflow-hidden rounded-xl border',
                media.length === 1 && 'grid-cols-1',
                media.length === 2 && 'grid-cols-2',
                media.length >= 3 && 'grid-cols-2',
              )}
            >
              {media.map((item, index) => (
                <div
                  key={item.path}
                  className={cn(
                    'bg-muted relative',
                    // X's own grid: a single image keeps a 16:9-ish crop; three
                    // images make the first one full-height on the left.
                    media.length === 1 ? 'aspect-[16/9]' : 'aspect-square',
                    media.length === 3 && index === 0 && 'row-span-2 aspect-auto',
                  )}
                >
                  <img src={item.url} alt={item.altText ?? ''} className="size-full object-cover" />
                </div>
              ))}
            </div>
          )}

          {/* Inert — this is a preview, not a client. Present because their
              absence makes the card read as "unfinished" rather than "a tweet". */}
          <div className="text-muted-foreground mt-3 flex max-w-xs items-center justify-between">
            {[MessageCircle, Repeat2, Heart, Share].map((Icon, i) => (
              <Icon key={i} className="size-4" aria-hidden />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
